<template>
  <article
    class="support-chat-message"
    :class="{ user: isUser, system: isSystem, failed: message.failed }"
    :aria-label="ariaLabel"
    :data-testid="message.testId"
  >
    <template v-if="isSystem">
      <div class="support-system-alert" :class="message.type" :role="message.type === 'error' ? 'alert' : 'status'">
        <a-icon :icon="systemIcon" width="20" height="20" />
        <div class="support-system-copy">
          <strong v-if="message.title">{{ message.title }}</strong>
          <div v-if="message.content" class="message-content" v-html="safeMarkdown(message.content)" />
          <small v-if="message.detail">{{ message.detail }}</small>
        </div>
      </div>
    </template>

    <template v-else>
      <img v-if="isAssistant" class="message-avatar" :src="systemLogo" alt="" />
      <div class="message-stack">
        <div class="message-meta">
          <strong>{{ isUser ? 'You' : 'ePeople Support' }}</strong>
          <span>{{ date }}</span>
        </div>
        <div class="message-bubble">
          <div v-if="message.content" class="message-content" v-html="safeMarkdown(message.content)" />
          <support-message-attachment
            v-if="message.attachment && conversationId"
            :attachment="message.attachment"
            :conversation-id="conversationId"
          />
          <small v-else-if="message.imageFile" class="failed-image-name">{{ message.imageFile.name }}</small>
        </div>
        <div v-if="message.failed" class="failed-message-status" role="alert">
          <span>{{ message.errorMessage || 'Message not sent.' }}</span>
          <button type="button" :disabled="retrying" @click="$emit('retry', message)">
            {{ retrying ? 'Retrying…' : 'Retry' }}
          </button>
        </div>
        <support-image-attachment
          v-if="message.failed && message.imageFile"
          :file="message.imageFile"
          :disabled="retrying"
          @select="$emit('replace-image', { message, file: $event })"
          @remove="$emit('remove-image', message)"
        />
        <slot />
      </div>
    </template>
  </article>
</template>

<script>
import { renderMarkdown } from '@/utils/safe-html'
import SupportMessageAttachment from './support-message-attachment.vue'
import SupportImageAttachment from './support-image-attachment.vue'

export default {
  components: {
    SupportMessageAttachment,
    SupportImageAttachment
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    date: {
      type: String,
      default: ''
    },
    conversationId: {
      type: [String, Number],
      default: null
    },
    systemLogo: {
      type: String,
      required: true
    },
    retrying: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isUser() {
      return this.message.role === 'user'
    },
    isAssistant() {
      return this.message.role === 'assistant'
    },
    isSystem() {
      return ['error', 'system', 'context', 'status'].includes(this.message.type) || (!this.isUser && !this.isAssistant)
    },
    ariaLabel() {
      if (this.isSystem) return this.message.title || 'Support chat status'
      return this.isUser ? 'Message from you' : 'Message from ePeople Support'
    },
    systemIcon() {
      return this.message.type === 'error' ? 'carbon:warning-alt' : 'carbon:information'
    }
  },
  methods: {
    safeMarkdown(text) {
      return renderMarkdown(text)
    }
  }
}
</script>

<style scoped>
.support-chat-message {
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 8px;
}

.support-chat-message.user {
  justify-content: flex-end;
}

.message-avatar {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  padding: 4px;
  object-fit: contain;
  border-radius: calc(var(--border-radius) / 2);
  border: 1px solid var(--border-color-base);
  background: var(--bg-component);
}

.message-stack {
  min-width: 0;
  max-width: 78%;
}

.message-meta {
  display: flex;
  gap: 8px;
  margin: 0 4px 4px;
  color: var(--text-color-alpha8);
  font-size: 12px;
}

.user .message-meta {
  justify-content: flex-end;
}

.message-bubble {
  min-width: 0;
  padding: 10px 12px;
  overflow-wrap: anywhere;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius) var(--border-radius) var(--border-radius) 4px;
  background: var(--bg-component);
  color: var(--text-color);
}

.user .message-bubble {
  border-color: #dbeafe;
  border-radius: var(--border-radius) var(--border-radius) 4px var(--border-radius);
  background: #eff6ff;
}

.theme-dark .user .message-bubble {
  border-color: #1e3a8a;
  background: #172554;
}

.failed .message-bubble {
  border-color: var(--error-color, #cf1322);
}

.message-content {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.message-content ::v-deep p:last-child {
  margin-bottom: 0;
}

.message-content ::v-deep pre,
.message-content ::v-deep code,
.message-content ::v-deep a {
  max-width: 100%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.failed-message-status {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 5px;
  color: var(--text-color);
  font-size: 12px;
}

.failed-message-status button {
  padding: 2px 4px;
  color: var(--text-color);
  font: inherit;
  font-weight: 600;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.failed-message-status button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.failed-image-name {
  display: block;
  margin-top: 6px;
  overflow-wrap: anywhere;
  color: var(--text-color-alpha8);
}

.support-system-alert {
  display: flex;
  width: 100%;
  gap: 10px;
  padding: 12px 14px;
  overflow-wrap: anywhere;
  color: var(--text-color);
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
  background: var(--bg-row-hover);
}

.support-system-alert.error {
  border-color: var(--error-color, #cf1322);
  background: var(--bg-row-hover);
}

.support-system-alert.status {
  border-color: var(--primary-color);
  background: var(--bg-row-hover);
}

.support-system-copy {
  min-width: 0;
}

.support-system-copy strong,
.support-system-copy small {
  display: block;
}

.support-system-copy small {
  margin-top: 2px;
  color: var(--text-color-alpha8);
}

@media (max-width: 520px) {
  .message-stack {
    max-width: 84%;
  }
}
</style>
